import { Circle, EyeOff } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { CustomInput } from "@/components/forms/customInput"
import { EyeIcon } from "@/components/icons"
import { AuthWrapper } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { clearTokens } from "@/lib/token-manager"
import { useAuthForm } from "../hooks/useAuthForm"
import { type ResetPasswordFormData, resetPasswordSchema } from "../schemas/auth.schema"
import { resetPassword } from "../services/auth.service"

export function ResetPasswordPage() {
  const { t } = useTranslation("auth")
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const token = searchParams.get("token") || ""
  const email = searchParams.get("email") || ""

  const [formState, setFormState] = useState<"default" | "success" | "error">("default")
  const [errorMessage, setErrorMessage] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)
  const [rightPos, setRightPos] = useState({ top: 0, left: 0 })
  const [belowPos, setBelowPos] = useState({ top: 0, left: 0 })
  const passwordSectionRef = useRef<HTMLDivElement>(null)

  const { form, handleSubmit, isSubmitting, serverError, clearServerError } = useAuthForm(
    resetPasswordSchema,
    async (data: ResetPasswordFormData) => {
      await resetPassword(email, token, data.password)
      setFormState("success")
    },
  )

  // Validate token on mount
  useEffect(() => {
    if (!token || !email) {
      setFormState("error")
      setErrorMessage(t("resetPassword.invalidToken"))
    }
  }, [token, email, t])

  const passwordValue = form.watch("password")
  const confirmPasswordValue = form.watch("confirmPassword")

  const passwordRegister = form.register("password")
  const confirmPasswordRegister = form.register("confirmPassword")

  const requirements = useMemo(
    () => [
      {
        label: t("passwordRequirements.minLength"),
        met: (passwordValue || "").length >= 8,
      },
      {
        label: t("passwordRequirements.uppercaseLowercase"),
        met: /[a-z]/.test(passwordValue || "") && /[A-Z]/.test(passwordValue || ""),
      },
      {
        label: t("passwordRequirements.number"),
        met: /\d/.test(passwordValue || ""),
      },
      {
        label: t("passwordRequirements.specialCharacter"),
        met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(passwordValue || ""),
      },
      {
        label: t("passwordRequirements.passwordsMatch"),
        met:
          (passwordValue || "").length > 0 &&
          (confirmPasswordValue || "").length > 0 &&
          passwordValue === confirmPasswordValue,
      },
    ],
    [passwordValue, confirmPasswordValue, t],
  )

  const updateTooltipPosition = useCallback(() => {
    if (passwordSectionRef.current) {
      const sectionRect = passwordSectionRef.current.getBoundingClientRect()
      const card = passwordSectionRef.current.closest('[style*="max-width"]')
      const cardRect = card?.getBoundingClientRect() ?? sectionRect
      setRightPos({ top: sectionRect.top, left: cardRect.right + 14 })
      setBelowPos({ top: sectionRect.bottom + 8, left: sectionRect.left })
    }
  }, [])

  // Track focus and position tooltip
  useEffect(() => {
    if (!passwordFocused) return

    updateTooltipPosition()

    const handleClickOutside = (e: MouseEvent) => {
      if (passwordSectionRef.current && !passwordSectionRef.current.contains(e.target as Node)) {
        const tooltipEls = document.querySelectorAll("[data-password-tooltip]")
        if ([...tooltipEls].some(el => el.contains(e.target as Node))) return
        setPasswordFocused(false)
      }
    }
    const handleFocusOutside = (e: FocusEvent) => {
      if (passwordSectionRef.current && !passwordSectionRef.current.contains(e.target as Node)) {
        setPasswordFocused(false)
      }
    }
    const handleLayoutChange = () => updateTooltipPosition()

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("focusin", handleFocusOutside)
    window.addEventListener("resize", handleLayoutChange)
    window.addEventListener("scroll", handleLayoutChange, true)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("focusin", handleFocusOutside)
      window.removeEventListener("resize", handleLayoutChange)
      window.removeEventListener("scroll", handleLayoutChange, true)
    }
  }, [passwordFocused, updateTooltipPosition])

  if (formState === "error") {
    return (
      <AuthWrapper>
        <div className="space-y-6 px-5 sm:px-10 md:px-20">
          <div className="text-center mb-5">
            <h2 className="text-2xl font-[500] text-primary font-base">
              {t("resetPassword.errorTitle")}
            </h2>
          </div>

          <div className="space-y-4 text-center">
            <p className="text-primary text-sm">
              {errorMessage || t("resetPassword.errorDescription")}
            </p>
          </div>

          <div className="py-2">
            <Button
              type="button"
              variant="primary-white"
              size="md"
              className="uppercase"
              fullWidth
              onClick={() => navigate("/forgot-password")}
            >
              {t("resetPassword.requestNewLink")}
            </Button>
          </div>
        </div>
      </AuthWrapper>
    )
  }

  if (formState === "success") {
    return (
      <AuthWrapper>
        <div className="space-y-6 px-5 sm:px-10 md:px-20">
          <div className="text-center mb-5">
            <h2 className="text-2xl font-[500] text-primary font-base">
              {t("resetPassword.successTitle")}
            </h2>
          </div>

          <div className="space-y-2 text-center">
            <p className="text-primary text-[13px]">{t("resetPassword.successDescription")}</p>
            <p className="text-primary text-[13px]">{t("resetPassword.successDescription2")}</p>
          </div>

          <div className="py-2 flex justify-center">
            <Button
              type="button"
              variant="primary-white"
              size="md"
              className="uppercase max-w-[200px]"
              fullWidth
              onClick={() => {
                // Clear tokens and navigate to login
                clearTokens()
                navigate("/login", { replace: true })
              }}
            >
              {t("resetPassword.loginButton")}
            </Button>
          </div>
        </div>
      </AuthWrapper>
    )
  }

  return (
    <AuthWrapper>
      <form onSubmit={handleSubmit}>
        <div className="space-y-6 px-5 sm:px-10 md:px-20">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-[500] text-primary font-base">
              {t("resetPassword.title")}
            </h2>
            <p className="text-primary text-sm mt-2">{t("resetPassword.description")}</p>
          </div>

          <div className="space-y-6 px-2" ref={passwordSectionRef}>
            <div className="relative">
              <CustomInput
                label={t("resetPassword.passwordLabel")}
                type={showPassword ? "text" : "password"}
                required={false}
                placeholder={t("resetPassword.passwordPlaceholder")}
                {...passwordRegister}
                error={
                  (form.formState.errors.password?.message || serverError) as string | undefined
                }
                disabled={isSubmitting}
                onChange={e => {
                  passwordRegister.onChange?.(e)
                  clearServerError()
                }}
                onFocus={() => setPasswordFocused(true)}
                className="pr-8"
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => !prev)}
                className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center text-body cursor-pointer"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff size={17} className="text-primary" />
                ) : (
                  <EyeIcon size={17} />
                )}
              </button>
            </div>

            <div className="relative">
              <CustomInput
                label={t("resetPassword.confirmPasswordLabel")}
                type={showConfirmPassword ? "text" : "password"}
                required={false}
                placeholder={t("resetPassword.confirmPasswordPlaceholder")}
                {...confirmPasswordRegister}
                error={form.formState.errors.confirmPassword?.message as string | undefined}
                disabled={isSubmitting}
                onChange={e => {
                  confirmPasswordRegister.onChange?.(e)
                  clearServerError()
                }}
                onFocus={() => setPasswordFocused(true)}
                className="pr-8"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(prev => !prev)}
                className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center text-body cursor-pointer"
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? (
                  <EyeOff size={17} className="text-primary" />
                ) : (
                  <EyeIcon size={17} />
                )}
              </button>
            </div>

            <div className="py-2 flex justify-center">
              <Button
                type="submit"
                variant="primary-white"
                size="md"
                className="uppercase max-w-[200px]"
                isLoading={isSubmitting}
                fullWidth
              >
                {t("resetPassword.submitButton")}
              </Button>
            </div>
          </div>
        </div>
      </form>
      {passwordFocused && (
        <PasswordRequirementsTooltip
          requirements={requirements}
          rightPos={rightPos}
          belowPos={belowPos}
          title={t("passwordRequirements.title")}
        />
      )}
    </AuthWrapper>
  )
}

function PasswordRequirementsTooltip({
  requirements,
  rightPos,
  belowPos,
  title,
}: {
  requirements: { label: string; met: boolean }[]
  rightPos: { top: number; left: number }
  belowPos: { top: number; left: number }
  title: string
}) {
  if (typeof document === "undefined") return null

  const content = (
    <div className="bg-[#1C1C1C] border border-dashed border-[#FFFFFF33] rounded-[20px] p-5 shadow-xl sm:w-[370px]">
      <p className="text-sm leading-6.25 font-semibold uppercase text-primary mb-2.5">{title}</p>
      <ul className="space-y-2.5">
        {requirements.map(req => (
          <li key={req.label} className="flex items-center gap-2.5">
            {req.met ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M10 1.6665C14.6024 1.6665 18.3334 5.39746 18.3334 9.99984C18.3334 14.6022 14.6024 18.3332 10 18.3332C5.39765 18.3332 1.66669 14.6022 1.66669 9.99984C1.66669 5.39746 5.39765 1.6665 10 1.6665ZM12.6831 7.47456L8.95835 11.1993L7.31696 9.5579C7.07288 9.31382 6.67716 9.31382 6.43308 9.5579C6.189 9.80197 6.189 10.1977 6.43308 10.4418L8.51641 12.5251C8.76049 12.7692 9.15622 12.7692 9.40029 12.5251L13.567 8.35845C13.811 8.11437 13.811 7.71864 13.567 7.47456C13.3229 7.23048 12.9272 7.23048 12.6831 7.47456Z"
                  fill="#5A5B3F"
                />
              </svg>
            ) : (
              <Circle size={20} className="text-[#5A5B3F] shrink-0" />
            )}
            <span className="text-[13px] leading-5 text-[#F2F0EBE6]">{req.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )

  return createPortal(
    <>
      <div
        className="hidden xl:block fixed z-50 transition-opacity duration-200"
        style={{ top: rightPos.top, left: rightPos.left }}
        data-password-tooltip
      >
        {content}
      </div>
      <div
        className="block xl:hidden fixed z-50 transition-opacity duration-200"
        style={{ top: belowPos.top, left: belowPos.left }}
        data-password-tooltip
      >
        {content}
      </div>
    </>,
    document.body,
  )
}
